import { RenderPaymentStatus } from '@/components/features/shared/utils'
import type { InvoiceData } from '@/domain/invoices/invoice'
import { PaymentStatus } from '@/domain/payments/payments'
import { BillingPeriodStatus } from '@/domain/subscriptions/subscriptions'
import type { UseMutateFunction } from '@tanstack/react-query'
import type { JSX } from 'react'
import { DownloadButton } from './DownloadButton'
import { CollectorActionButton, DeleteButton, EditButton, PayButton } from './PaymentActionButtons'
import { PaymentRow } from './PaymentRow'

type ReturnPaymentModalState = { type: 'returnPayment'; payment: PaymentRow }

interface RenderActionsCellDeps {
  invoices: InvoiceData[]
  canPay: boolean
  onSubscriptionPaymentRequest: (data: { billingPeriodId: string; paymentId: string }) => void
  onNewSubscriptionPaymentRequest: (contractId: string, billingPeriodId: string) => void
  onDeleteDirectPayment: (paymentId: string, contractId: string) => void
  onDeletePeriodPayment: (subscriptionId: string, billingPeriodId: string) => void
  onPaymentRequest: (data: { paymentId?: string; contractId: string; amountOwed?: number }) => void
  onEditDescription?: (payment: PaymentRow) => void
  openModal: (state: ReturnPaymentModalState) => void
  onOpenDocument: UseMutateFunction<any, any, { documentId: string; fileName: string }, unknown>
}

const canBeDeleted = (status: PaymentStatus) =>
  status !== PaymentStatus.COMPLETED && status !== PaymentStatus.IN_COLLECTOR

const canBeCollected = (status: PaymentStatus, collectorId: string | null | undefined) =>
  status === PaymentStatus.IN_COLLECTOR && collectorId

const canBeDownloaded = (invoices: InvoiceData[]) => invoices.length > 0

const isCompleted = (status: PaymentStatus) =>
  status === PaymentStatus.COMPLETED || status === PaymentStatus.IN_COLLECTOR

export const renderSubscriptionActions = (
  row: PaymentRow,
  deps: RenderActionsCellDeps,
): JSX.Element | null => {
  const { referenceId, billingPeriodId, periodStatus, outstandingAmount, hasPendingPayment } = row.__context
  const { invoices, canPay, onSubscriptionPaymentRequest, onNewSubscriptionPaymentRequest, onDeleteDirectPayment, onDeletePeriodPayment, onEditDescription, openModal, onOpenDocument } = deps

  if (!referenceId) return <RenderPaymentStatus paymentStatus={row.status} />
  if (!billingPeriodId) return <RenderPaymentStatus paymentStatus={row.status} />

  const isCancelled = periodStatus === BillingPeriodStatus.CANCELLED
  const isSubCompleted = (outstandingAmount ?? 0) <= 0 && !isCancelled
  const isPartial = periodStatus === BillingPeriodStatus.PARTIAL
  const isPayCompleted = isCompleted(row.status)
  const isPayPending = row.status === PaymentStatus.PENDING
  const isPayRefunded = row.status === PaymentStatus.REFUNDED

  const showPayButton = !isSubCompleted && (!isPayCompleted || (isPayCompleted && isPartial && !hasPendingPayment))
  const showPayAction = canPay && showPayButton && (isPayPending || ((!isCancelled || isPayRefunded) && (!isPartial || isPayRefunded)))
    && (!isPayRefunded || (isPayRefunded && !hasPendingPayment))

  return (
    <div className="flex items-center gap-3">
      {(showPayAction || isPayRefunded) && (
        <PayButton onClick={() => isPayCompleted
          ? onNewSubscriptionPaymentRequest(referenceId, billingPeriodId)
          : onSubscriptionPaymentRequest({ billingPeriodId, paymentId: row.id })} />
      )}
      {onEditDescription && isPayPending && row.description && (
        <EditButton onClick={() => onEditDescription(row)} />
      )}
      {canBeDeleted(row.status) && referenceId && billingPeriodId && (
        <DeleteButton onClick={() => hasPendingPayment && isPartial
          ? onDeleteDirectPayment(row.id, referenceId)
          : onDeletePeriodPayment(referenceId, billingPeriodId)} />
      )}
      {canBeCollected(row.status, row.collectorId) && (
        <CollectorActionButton onClick={() => openModal({ type: 'returnPayment', payment: row })} />
      )}
      {canBeDownloaded(invoices) && (
        <DownloadButton invoices={invoices} onOpenDocument={onOpenDocument} />
      )}
    </div>
  )
}

export const renderInstallmentActions = (
  row: PaymentRow,
  deps: RenderActionsCellDeps,
): JSX.Element | null => {
  const { referenceId } = row.__context
  const { invoices, canPay, onPaymentRequest, onDeleteDirectPayment, openModal, onOpenDocument } = deps

  const isPayable = row.status === PaymentStatus.PENDING || row.status === PaymentStatus.REFUNDED
  return (
    <div className="flex items-center gap-3">
      {canPay && isPayable && (
        <PayButton onClick={() => onPaymentRequest({ paymentId: row.id, contractId: referenceId! })} />
      )}
      {canBeDeleted(row.status) && (
        <DeleteButton onClick={() => onDeleteDirectPayment(row.id, referenceId!)} />
      )}
      {canBeCollected(row.status, row.collectorId) && (
        <CollectorActionButton onClick={() => openModal({ type: 'returnPayment', payment: row })} />
      )}
      {canBeDownloaded(invoices) && (
        <DownloadButton invoices={invoices} onOpenDocument={onOpenDocument} />
      )}
    </div>
  )
}

export const renderDirectActions = (
  row: PaymentRow,
  deps: RenderActionsCellDeps,
): JSX.Element | null => {
  const { referenceId } = row.__context
  const { invoices, canPay, onPaymentRequest, onDeleteDirectPayment, openModal, onOpenDocument } = deps

  const isPayable = row.status === PaymentStatus.PENDING || row.status === PaymentStatus.REFUNDED
  return (
    <div className="flex items-center gap-3">
      {canPay && isPayable && (
        <PayButton onClick={() => onPaymentRequest({ contractId: referenceId!, paymentId: row.id })} />
      )}
      {canBeDeleted(row.status) && (
        <DeleteButton onClick={() => onDeleteDirectPayment(row.id, referenceId)} />
      )}
      {canBeCollected(row.status, row.collectorId) && (
        <CollectorActionButton onClick={() => openModal({ type: 'returnPayment', payment: row })} />
      )}
      {canBeDownloaded(invoices) && (
        <DownloadButton invoices={invoices} onOpenDocument={onOpenDocument} />
      )}
    </div>
  )
}
